import React, { useCallback, type MouseEvent } from 'react';
import { styled } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { CardId } from '../../../../../../../common/types.tsx';
import messages from './messages.tsx';

type IssueKeyFieldProps = {
	issueId: CardId;
	issueKey: string;
	onDetailClick: (id: CardId) => void;
};

const IssueKeyField = ({ issueId, issueKey, onDetailClick }: IssueKeyFieldProps) => {
	const { formatMessage } = useIntl();

	const onClick = useCallback(
		(e: MouseEvent<HTMLElement>, analyticsEvent: UIAnalyticsEvent) => {
			e.stopPropagation();

			const withCmd = e.ctrlKey || e.metaKey;
			// ensure this doesn't interfere with opening the link in a new window
			if (!withCmd) {
				e.preventDefault();
				onDetailClick(issueId);
			}

			fireUIAnalytics(analyticsEvent, 'onEpicPanelIssueKeyClick', { withCmd });
		},
		[issueId, onDetailClick],
	);

	return (
		<Container>
			<Text>{formatMessage(messages.label)}</Text>
			<StyledButton
				href={`/browse/${issueKey}`}
				target="_blank"
				onClick={onClick}
				appearance="subtle-link"
				spacing="none"
			>
				{issueKey}
			</StyledButton>
		</Container>
	);
};

export default IssueKeyField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.div({
	display: 'flex',
	justifyContent: 'space-between',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledButton = styled(Button)({
	padding: 0,
	fontWeight: token('font.weight.regular'),
	color: token('color.text.subtlest'),
});
